import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, BookOpen, Truck, FileText, ClipboardCheck, Send, Wrench, Boxes, ShoppingCart, Layers, ShieldCheck, Wifi, Database, QrCode } from 'lucide-react';

/*
 * SystemGuide.jsx
 * Single-page, printable user guide. Click "Download PDF" → browser print dialog.
 * Designed to look identical on screen and on paper (A4). All flowcharts are
 * inline SVG so no library / external image is required.
 */

const SystemGuide = () => {
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/settings/branding'); setBranding(r.data); } catch (e) { /* ignore */ }
    })();
  }, []);

  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-4">
      {/* On-screen action bar (hidden when printing) */}
      <div className="print:hidden flex items-center justify-between border-b pb-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="guide-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <Button size="sm" onClick={() => window.print()} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="guide-print-btn">
          <Printer className="h-4 w-4 mr-1.5" /> Download as PDF
        </Button>
      </div>

      {/* The printable document */}
      <article className="bg-white mx-auto max-w-4xl text-slate-900 print:max-w-none" id="guide-doc" data-testid="guide-doc">

        {/* ---------- COVER ---------- */}
        <section className="px-10 pt-10 pb-6 border-b-2 border-slate-900 print:break-after-page">
          <div className="flex items-center gap-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="h-16 w-16 object-contain" />
            ) : (
              <div className="h-16 w-16 rounded bg-[hsl(217,91%,24%)] grid place-items-center text-white font-bold text-xl">PIVI</div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{branding?.header_text || 'PIVI EQUIPMENT SOLUTIONS DEPARTMENT'}</div>
              <h1 className="text-3xl font-bold tracking-tight mt-1">ESD – Equipment Solutions Department</h1>
              <div className="text-sm text-slate-600">Complete User Guide &amp; Workflow Reference</div>
            </div>
          </div>

          <div className="mt-12 max-w-2xl">
            <p className="text-lg text-slate-700 leading-relaxed">A practical, picture‑first walkthrough of every workflow in your motor pool — from the moment a project site asks for equipment all the way through paperless handover, on‑site usage tracking, and consumables purchasing.</p>
            <p className="mt-4 text-sm text-slate-500">Use the table of contents on the next page to jump straight to the workflow you need. Print this document as a quick‑reference manual for every operator and supervisor on site.</p>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-6 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Generated</div><div className="font-semibold">{today}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Version</div><div className="font-semibold">ESD v1.0</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Audience</div><div className="font-semibold">Operators · Supervisors · Admins</div></div>
          </div>
        </section>

        {/* ---------- TOC ---------- */}
        <Section title="Table of Contents" icon={BookOpen} pageBreak>
          <ol className="space-y-1.5 text-sm list-decimal list-inside marker:text-[hsl(217,91%,24%)] marker:font-bold">
            <li>System at a Glance — architecture &amp; modules</li>
            <li>User Roles &amp; Permissions</li>
            <li>The Core Workflow — request → mobilization</li>
            <li>Equipment Queues — alternative vehicle flow</li>
            <li>Mobilization Form — QR, signatures, offline &amp; tracking</li>
            <li>Equipment Location &amp; Photos — geo-tag &amp; condition shots</li>
            <li>Consumables / Spare Parts — usage logging</li>
            <li>Purchase Requests — request → received → closed</li>
            <li>Maintenance, Utilizations, Contracts &amp; Billing</li>
            <li>Roles, Approvals &amp; Settings</li>
            <li>Offline‑First — what happens with no signal</li>
            <li>Data Management — backup, restore &amp; purge</li>
            <li>Glossary of Statuses</li>
          </ol>
        </Section>

        {/* ---------- 1. SYSTEM AT A GLANCE ---------- */}
        <Section title="1 · System at a Glance" icon={Truck} pageBreak>
          <p className="text-sm">ESD is one place for everything your equipment department does: equipment, operators, requests, inspections, mobilizations, consumables, purchases and maintenance. Each module feeds the next so you never re‑type data.</p>
          <ArchDiagram />
          <BulletGrid items={[
            { i: Truck, t: 'Equipment', d: 'Master record of every machine: code, plate, status, location.' },
            { i: ClipboardCheck, t: 'Operators', d: 'Drivers with licenses and NC II certificates.' },
            { i: FileText, t: 'Requests', d: 'Project sites ask for a piece of equipment.' },
            { i: ClipboardCheck, t: 'Inspections', d: 'Pre‑deployment check with PDF report.' },
            { i: Send, t: 'Mobilizations', d: 'Paperless handover form with QR + signatures.' },
            { i: Boxes, t: 'Consumables', d: 'Oil, filters, tires — every cost tracked.' },
            { i: ShoppingCart, t: 'Purchase Requests', d: 'Stock low? One tap creates a task.' },
            { i: Wrench, t: 'Maintenance', d: 'Schedule, log and close work orders.' },
          ]} />
        </Section>

        {/* ---------- 2. ROLES ---------- */}
        <Section title="2 · User Roles & Permissions" icon={ShieldCheck} pageBreak>
          <p className="text-sm">Six built‑in roles ship with ESD. Super admins can also create unlimited custom roles in <em>Settings → Roles &amp; Permissions</em>.</p>
          <table className="w-full text-xs mt-3 border border-slate-200">
            <thead className="bg-slate-50 text-left">
              <tr><th className="p-2 border-b border-slate-200">Role</th><th className="p-2 border-b border-slate-200">Typical user</th><th className="p-2 border-b border-slate-200">Headline powers</th></tr>
            </thead>
            <tbody>
              {ROLE_ROWS.map((r) => (
                <tr key={r.role} className="border-b border-slate-100 align-top">
                  <td className="p-2 font-semibold whitespace-nowrap">{r.role}</td>
                  <td className="p-2 text-slate-600">{r.who}</td>
                  <td className="p-2 text-slate-700">{r.power}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-2 italic">Tip: the Approval Workflow page (Settings → Approval Workflow) lets you nominate WHICH roles or specific users approve each entity type — equipment requests, inspections, mobilizations, maintenance and purchase requests.</p>
        </Section>

        {/* ---------- 3. CORE WORKFLOW ---------- */}
        <Section title="3 · The Core Workflow" icon={FileText} pageBreak>
          <p className="text-sm">From the project site&rsquo;s ask to the equipment rolling out the gate, ESD walks the request through five named states. Each transition is audited and notifies the next person in line.</p>
          <CoreFlowDiagram />
          <ol className="text-sm list-decimal list-inside space-y-2 mt-4">
            <li><strong>Request</strong> — a requestor on a project site submits an Equipment Request specifying category, quantity, dates, and purpose.</li>
            <li><strong>Approval</strong> — the configured approver (default: Approver/Admin) approves and assigns a specific piece of equipment.</li>
            <li><strong>Inspection</strong> — supervisor uploads the inspection PDF; required before approval.</li>
            <li><strong>Mobilization</strong> — on inspection approval, a mobilization is automatically created and the printable Mobilization Form opens.</li>
            <li><strong>Completion</strong> — when the equipment returns and mobilization is marked Completed, the vehicle goes back to idle and the request closes.</li>
          </ol>
        </Section>

        {/* ---------- 4. EQUIPMENT QUEUES ---------- */}
        <Section title="4 · Equipment Queues" icon={Layers} pageBreak>
          <p className="text-sm">During inspection, if the chosen equipment turns out to be unavailable, damaged or not yet on site, send it to the Equipment Queue. The queuing team finds a like‑for‑like alternative in the same category and routes the inspection back to pending.</p>
          <QueueDiagram />
          <ul className="text-sm list-disc list-inside space-y-1 mt-3">
            <li>Queues are grouped by equipment <strong>category</strong>, so substitutions are always like‑for‑like.</li>
            <li>If no alternative is operational, the request waits in the category queue until one becomes available.</li>
            <li>Cancelling a queue entry restores the original inspection to pending status.</li>
          </ul>
        </Section>

        {/* ---------- 5. MOBILIZATION FORM ---------- */}
        <Section title="5 · Mobilization Form (Paperless Handover)" icon={QrCode} pageBreak>
          <p className="text-sm">A printable, scannable hand‑over document generated for every mobilization. It carries the equipment&rsquo;s serial, a QR code for live tracking, and three lockable digital signatures.</p>
          <MobilizationDiagram />
          <BulletGrid items={[
            { i: QrCode, t: 'Unique serial + QR', d: 'MOB‑YYYYMMDD‑NNNN. The QR opens a public tracking page anyone can scan without login.' },
            { i: ClipboardCheck, t: '3 signatures', d: 'Coordinator releases, Operator receives, Site Receiver confirms arrival. Each locks once captured.' },
            { i: Wifi, t: 'Offline‑first', d: 'Drafts autosave to the device. Submissions queue until signal returns.' },
            { i: Printer, t: 'Print → PDF', d: 'Browser print dialog produces a clean A4 PDF — no extra app required.' },
          ]} />
          <p className="text-[11px] text-slate-500 mt-3 italic">Branding (logo, header, footer disclaimer, tracking URL) can be customized in <strong>Settings → Form Branding</strong> at any time.</p>
        </Section>

        {/* ---------- 6. EQUIPMENT LOCATION & PHOTOS ---------- */}
        <Section title="6 · Equipment Location & Photos" icon={Truck} pageBreak>
          <p className="text-sm">Every equipment row has a pin button to drop its current location and a camera button to attach condition photos. Useful for tracking where idle machines are parked, recording damage before mobilization, and giving operators a visual reference on day one.</p>
          <LocationPhotosDiagram />
          <h3 className="text-sm font-bold mt-4 mb-2 uppercase tracking-wider text-slate-700">Location tab</h3>
          <ul className="text-sm list-disc list-inside space-y-1">
            <li>Type lat/lng manually <strong>or</strong> tap <em>“Use my GPS”</em> to capture the device&rsquo;s coordinates via the browser&rsquo;s geolocation API.</li>
            <li>Add an optional human label (e.g. <em>“Quarry north entrance”</em>).</li>
            <li>Every change is appended to a history log so you can audit movement over time.</li>
            <li>One-click <em>“Open in Maps”</em> link launches Google Maps at that exact pin.</li>
          </ul>
          <h3 className="text-sm font-bold mt-4 mb-2 uppercase tracking-wider text-slate-700">Photos tab</h3>
          <ul className="text-sm list-disc list-inside space-y-1">
            <li>Drag-drop or pick files from camera roll. Each image is compressed to ≤200 KB JPEG in the browser before upload — friendly to slow data plans.</li>
            <li>Up to 12 active photos per equipment. Add an optional caption to each.</li>
            <li>From the Equipment grid, a <strong>camera icon</strong> appears next to any row that has approved photos — tap it to open the read-only Photo Lightbox with thumbnails, prev/next arrows, captions and a download button.</li>
          </ul>
          <h3 className="text-sm font-bold mt-4 mb-2 uppercase tracking-wider text-slate-700">Who can do what</h3>
          <p className="text-sm">Configured via <em>Settings → Roles &amp; Permissions</em> under modules <code>equipment_location</code> (view/edit/approve) and <code>equipment_photos</code> (view/upload/delete/approve). If the Approval Workflow has it enabled, non-approver submissions arrive as <strong>pending</strong>; an Approver/Admin then accepts or rejects them — and only approved photos appear in the public lightbox.</p>
          <p className="text-[11px] text-slate-500 mt-3 italic">Tip: photos and location updates each leave an entry in the Audit Trail so you can trace who changed what, when (timestamps shown in GMT+8).</p>
        </Section>

        {/* ---------- 7. CONSUMABLES ---------- */}
        <Section title="7 · Consumables / Spare Parts" icon={Boxes} pageBreak>
          <p className="text-sm">Every drop of oil, every filter, every brake pad carries a price. Track it once, see cost trends per asset and per item — and stop bleeding budget on untracked supplies.</p>
          <ConsumablesDiagram />
          <h3 className="text-sm font-bold mt-4 mb-2 uppercase tracking-wider text-slate-700">What you record per transaction</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {['Date', 'Equipment ID', 'Meter / Hours', 'Item name', 'Brand / Part #', 'Quantity & unit', 'Unit price', 'Total cost (auto)', 'Technician', 'Work order (optional)', 'Reason', 'Remarks']
              .map((f) => <div key={f} className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded font-medium text-slate-700">{f}</div>)}
          </div>
          <h3 className="text-sm font-bold mt-4 mb-1 uppercase tracking-wider text-slate-700">Price visibility</h3>
          <p className="text-sm">For every item the system shows <strong>last purchase price</strong> and <strong>running average</strong> — easy to spot a supplier hiking prices. Roll‑ups by equipment feed your Total Cost of Ownership reports.</p>
        </Section>

        {/* ---------- 8. PURCHASE REQUEST ---------- */}
        <Section title="8 · Purchase Requests" icon={ShoppingCart} pageBreak>
          <p className="text-sm">When stock is low or a specific spare part is needed for an upcoming repair, anyone can file a Purchase Request. It becomes an assignable task — not an SMS lost in WhatsApp.</p>
          <PurchaseStateMachine />
          <ol className="text-sm list-decimal list-inside space-y-1.5 mt-3">
            <li><strong>Trigger</strong> — low stock, breakdown, or upcoming preventive job.</li>
            <li><strong>Submit</strong> — item, qty, est. unit price, reason, priority, delivery date; optional link to equipment / work order.</li>
            <li><strong>Assignment</strong> — auto‑assigned to the configured purchasing officer / approver as a tracked task.</li>
            <li><strong>Status</strong> — Pending → Approved → Ordered → Received → Closed (or Rejected at any step before Received).</li>
            <li><strong>Auto‑log</strong> — when marked Received, ESD auto‑creates a Consumables log entry with the actual price.</li>
            <li><strong>Notify</strong> — the original requester is pinged at every transition.</li>
          </ol>
          <p className="text-[11px] text-slate-500 mt-3 italic">Authorization: only Approvers/Admins can approve or reject. Only the assignee or an Admin can mark Ordered/Received/Closed.</p>
        </Section>

        {/* ---------- 9. MAINTENANCE / UTILIZATIONS / CONTRACTS / BILLING ---------- */}
        <Section title="9 · Maintenance, Utilizations, Contracts & Billing" icon={Wrench} pageBreak>
          <BulletGrid items={[
            { i: Wrench, t: 'Maintenance', d: 'Schedule preventive jobs, log corrective repairs, link consumables and purchase requests. Vehicle status auto‑moves to “maintenance” while a job is open.' },
            { i: Boxes, t: 'Utilizations', d: 'Capture hours used, fuel consumed and odometer per shift to power utilization & efficiency KPIs.' },
            { i: FileText, t: 'Contracts', d: 'Track client agreements: dates, total value, assigned equipment list.' },
            { i: FileText, t: 'Billings', d: 'Generate invoices against contracts. Track Paid / Pending / Overdue.' },
          ]} />
        </Section>

        {/* ---------- 10. ROLES, APPROVALS, SETTINGS ---------- */}
        <Section title="10 · Settings — Roles, Approvals & More" icon={ShieldCheck} pageBreak>
          <BulletGrid items={[
            { i: ShieldCheck, t: 'Roles & Permissions', d: 'Create custom roles and tick exactly which actions they can perform across every module.' },
            { i: ClipboardCheck, t: 'Approval Workflow', d: 'Pick approver roles or specific users per entity: equipment requests, inspections, mobilizations, maintenance, purchase requests.' },
            { i: BookOpen, t: 'Dropdowns / Add Company', d: 'Manage centralized lists (areas, locations, categories, NC II certificates, consumable types).' },
            { i: Printer, t: 'Form Branding', d: 'Header text, logo, footer disclaimer, and tracking URL for the Mobilization Form.' },
          ]} />
        </Section>

        {/* ---------- 11. OFFLINE ---------- */}
        <Section title="11 · Offline‑First — What happens with no signal" icon={Wifi} pageBreak>
          <p className="text-sm">Field sites lose signal. ESD forms keep working anyway. Each form auto‑saves a draft to your device&rsquo;s local storage every couple of seconds. When you submit while offline, the request is added to a local queue; when your device reconnects, the queue drains automatically.</p>
          <OfflineDiagram />
          <ul className="text-sm list-disc list-inside space-y-1 mt-3">
            <li><strong>Drafts</strong> survive page reload, browser restart, even device restart.</li>
            <li><strong>Queued submissions</strong> are stamped with a `queued_at` timestamp so you know what&rsquo;s pending.</li>
            <li><strong>Mobilization QR scans</strong> show the last cached payload while offline, then refresh as soon as you&rsquo;re online again.</li>
          </ul>
        </Section>

        {/* ---------- 12. DATA MANAGEMENT ---------- */}
        <Section title="12 · Data Management" icon={Database} pageBreak>
          <BulletGrid items={[
            { i: Database, t: 'Backup', d: 'One click downloads a JSON snapshot of every collection. Keep it off‑site.' },
            { i: Database, t: 'Restore', d: 'Upload a JSON backup to replay every record. Useful for staging.' },
            { i: Database, t: 'Purge', d: 'Wipe everything except the super.admin user. Requires password confirmation. Audit trail is preserved.' },
          ]} />
          <p className="text-[11px] text-slate-500 mt-3 italic">Audit trail is immutable. Every create / update / delete is logged with user, timestamp and before/after.</p>
        </Section>

        {/* ---------- 13. GLOSSARY ---------- */}
        <Section title="13 · Glossary of Statuses" icon={BookOpen}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <Glossary title="Vehicle status">
              <li><b>operational</b> — ready or in active use</li>
              <li><b>idle</b> — available, awaiting assignment</li>
              <li><b>reserved</b> — earmarked for an upcoming mobilization</li>
              <li><b>maintenance</b> — currently being serviced</li>
              <li><b>breakdown</b> — out of order, needs repair</li>
              <li><b>inefficient / for_disposal / scrap</b> — end‑of‑life states</li>
            </Glossary>
            <Glossary title="Request / Inspection">
              <li><b>pending</b> — awaiting approver</li>
              <li><b>approved</b> — assigned, inspection due</li>
              <li><b>rejected</b> — denied (mandatory reason)</li>
              <li><b>deployed / completed</b> — mobilization done / returned</li>
            </Glossary>
            <Glossary title="Mobilization">
              <li><b>pending</b> — created, awaiting departure</li>
              <li><b>in_progress</b> — equipment is mobilized</li>
              <li><b>completed</b> — returned, request closed</li>
              <li><b>cancelled</b> — voided</li>
            </Glossary>
            <Glossary title="Purchase Request">
              <li><b>pending → approved → ordered → received → closed</b></li>
              <li><b>rejected</b> — denied before receipt</li>
              <li>Auto‑creates a Consumables log on receipt.</li>
            </Glossary>
          </div>
        </Section>

        {/* ---------- FOOTER ---------- */}
        <footer className="px-10 py-4 border-t text-[10px] text-center text-slate-500 italic">
          {branding?.footer_disclaimer || 'This is a computer-generated document and does not require a signature for reference purposes.'}
          <div className="mt-1">ESD – Equipment Solutions Department · User Guide · {today} · All timestamps shown in GMT+8 (Asia/Manila)</div>
        </footer>
      </article>
    </div>
  );
};

/* ---------------- Layout primitives ---------------- */
const Section = ({ title, icon: Icon, pageBreak, children }) => (
  <section className={`px-10 py-7 border-t border-slate-200 ${pageBreak ? 'print:break-before-page' : ''}`}>
    <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-[hsl(217,91%,24%)] mb-4">
      {Icon && <Icon className="h-5 w-5" />}
      <span>{title}</span>
    </h2>
    <div className="space-y-2 text-slate-800">{children}</div>
  </section>
);

const BulletGrid = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
    {items.map((b) => (
      <div key={b.t} className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
        <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm"><b.i className="h-4 w-4 text-[hsl(217,91%,24%)]" /> {b.t}</div>
        <p className="text-xs text-slate-600 mt-1">{b.d}</p>
      </div>
    ))}
  </div>
);

const Glossary = ({ title, children }) => (
  <div className="border border-slate-200 rounded p-3 bg-white">
    <div className="font-semibold text-sm text-slate-900 mb-1">{title}</div>
    <ul className="list-disc list-inside space-y-0.5 text-slate-700">{children}</ul>
  </div>
);

/* ---------------- Flow diagrams (inline SVG) ---------------- */
const Box = ({ x, y, w = 110, h = 38, label, sub, tone = 'primary' }) => {
  const palette = { primary: { fill: '#dbeafe', stroke: '#1e40af', label: '#1e3a8a' }, emerald: { fill: '#d1fae5', stroke: '#047857', label: '#065f46' }, amber: { fill: '#fef3c7', stroke: '#b45309', label: '#92400e' }, slate: { fill: '#e2e8f0', stroke: '#475569', label: '#1e293b' }, red: { fill: '#fee2e2', stroke: '#b91c1c', label: '#7f1d1d' }, indigo: { fill: '#e0e7ff', stroke: '#4338ca', label: '#3730a3' } }[tone];
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={6} fill={palette.fill} stroke={palette.stroke} strokeWidth={1.4} />
      <text x={x + w / 2} y={y + (sub ? 16 : 23)} textAnchor="middle" fontSize={11} fontWeight={700} fill={palette.label}>{label}</text>
      {sub && <text x={x + w / 2} y={y + 28} textAnchor="middle" fontSize={9} fill={palette.label} opacity={0.75}>{sub}</text>}
    </g>
  );
};

const Arrow = ({ x1, y1, x2, y2, label, dashed = false }) => {
  const id = `arrow-${x1}-${y1}-${x2}-${y2}`;
  return (
    <g>
      <defs>
        <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#475569" />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#475569" strokeWidth={1.4} strokeDasharray={dashed ? '4 3' : ''} markerEnd={`url(#${id})`} />
      {label && <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4} textAnchor="middle" fontSize={9} fill="#475569" fontStyle="italic">{label}</text>}
    </g>
  );
};

const ArchDiagram = () => (
  <svg viewBox="0 0 720 200" className="w-full mt-3" role="img" aria-label="System architecture">
    <Box x={20} y={30} label="Frontend" sub="React · Tailwind" tone="primary" />
    <Box x={20} y={130} label="Mobile / QR" sub="Public tracking" tone="primary" />
    <Box x={290} y={80} label="ESD API" sub="FastAPI · JWT" tone="indigo" w={140} />
    <Box x={560} y={30} label="MongoDB" sub="Collections" tone="emerald" />
    <Box x={560} y={130} label="Audit Trail" sub="Immutable" tone="slate" />
    <Arrow x1={130} y1={50} x2={290} y2={95} />
    <Arrow x1={130} y1={150} x2={290} y2={110} />
    <Arrow x1={430} y1={90} x2={560} y2={50} />
    <Arrow x1={430} y1={110} x2={560} y2={150} />
  </svg>
);

const CoreFlowDiagram = () => (
  <svg viewBox="0 0 720 220" className="w-full mt-3" role="img" aria-label="Core workflow">
    <Box x={10} y={20} label="Request" sub="requestor" tone="primary" />
    <Box x={150} y={20} label="Approved" sub="approver" tone="indigo" />
    <Box x={290} y={20} label="Inspection" sub="supervisor + PDF" tone="amber" />
    <Box x={430} y={20} label="Mobilization" sub="auto‑created" tone="emerald" />
    <Box x={570} y={20} label="Completed" sub="vehicle → idle" tone="slate" />
    <Arrow x1={120} y1={39} x2={150} y2={39} />
    <Arrow x1={260} y1={39} x2={290} y2={39} />
    <Arrow x1={400} y1={39} x2={430} y2={39} />
    <Arrow x1={540} y1={39} x2={570} y2={39} />
    <Box x={290} y={130} label="Send to Queue" sub="(see §4)" tone="amber" />
    <Box x={150} y={130} label="Rejected" sub="with reason" tone="red" />
    <Arrow x1={345} y1={60} x2={345} y2={130} label="unavailable / damaged" dashed />
    <Arrow x1={210} y1={60} x2={210} y2={130} label="declined" dashed />
  </svg>
);

const QueueDiagram = () => (
  <svg viewBox="0 0 720 170" className="w-full mt-3" role="img" aria-label="Equipment queue flow">
    <Box x={20} y={20} label="Inspection" sub="pending" tone="amber" />
    <Box x={170} y={20} label="Queued" sub="awaiting alt." tone="amber" />
    <Box x={330} y={20} label="Find Alternative" sub="same category" tone="primary" />
    <Box x={500} y={20} label="Assigned" sub="back to inspection" tone="emerald" />
    <Arrow x1={130} y1={39} x2={170} y2={39} label="send to queue" />
    <Arrow x1={280} y1={39} x2={330} y2={39} />
    <Arrow x1={440} y1={39} x2={500} y2={39} />
    <Box x={170} y={110} label="No alt. yet" sub="wait in queue" tone="slate" />
    <Arrow x1={225} y1={60} x2={225} y2={110} dashed />
    <Box x={330} y={110} label="Cancel queue" sub="back to pending" tone="red" />
    <Arrow x1={380} y1={60} x2={380} y2={110} dashed />
  </svg>
);

const MobilizationDiagram = () => (
  <svg viewBox="0 0 720 200" className="w-full mt-3" role="img" aria-label="Mobilization form">
    <Box x={20} y={20} label="Inspection" sub="approved" tone="amber" />
    <Box x={180} y={20} label="Mobilization Form" sub="serial + QR" tone="emerald" w={140} />
    <Box x={370} y={20} label="3 Signatures" sub="locked once captured" tone="primary" w={140} />
    <Box x={560} y={20} label="Print → PDF" sub="A4" tone="slate" />
    <Arrow x1={130} y1={39} x2={180} y2={39} />
    <Arrow x1={320} y1={39} x2={370} y2={39} />
    <Arrow x1={510} y1={39} x2={560} y2={39} />
    <Box x={180} y={130} label="QR Public Tracker" sub="/track/{token}" tone="indigo" w={140} />
    <Box x={370} y={130} label="Status History" sub="auto‑logged" tone="slate" w={140} />
    <Arrow x1={250} y1={60} x2={250} y2={130} dashed label="scan" />
    <Arrow x1={440} y1={60} x2={440} y2={130} dashed />
  </svg>
);

const ConsumablesDiagram = () => (
  <svg viewBox="0 0 720 180" className="w-full mt-3" role="img" aria-label="Consumables flow">
    <Box x={20} y={30} label="Log Usage" sub="qty × unit price" tone="primary" w={120} />
    <Box x={170} y={30} label="Auto Total" sub="₱ computed" tone="emerald" w={120} />
    <Box x={320} y={30} label="Per‑item KPI" sub="avg & last price" tone="indigo" w={120} />
    <Box x={470} y={30} label="Per‑equipment" sub="TCO rollup" tone="indigo" w={120} />
    <Arrow x1={140} y1={49} x2={170} y2={49} />
    <Arrow x1={290} y1={49} x2={320} y2={49} />
    <Arrow x1={440} y1={49} x2={470} y2={49} />
    <Box x={170} y={120} label="Linked to Equipment" sub="& Work Order" tone="slate" w={130} />
    <Box x={340} y={120} label="Cost Trends" sub="dashboard" tone="slate" w={120} />
    <Arrow x1={230} y1={68} x2={230} y2={120} dashed />
    <Arrow x1={400} y1={68} x2={400} y2={120} dashed />
  </svg>
);

const PurchaseStateMachine = () => (
  <svg viewBox="0 0 720 200" className="w-full mt-3" role="img" aria-label="Purchase request state machine">
    <Box x={10} y={30} label="Pending" sub="auto‑assigned" tone="amber" />
    <Box x={150} y={30} label="Approved" sub="approver" tone="primary" />
    <Box x={290} y={30} label="Ordered" sub="+ PO #" tone="indigo" />
    <Box x={430} y={30} label="Received" sub="+ actual price" tone="emerald" />
    <Box x={570} y={30} label="Closed" sub="logged" tone="slate" />
    <Arrow x1={120} y1={49} x2={150} y2={49} />
    <Arrow x1={260} y1={49} x2={290} y2={49} />
    <Arrow x1={400} y1={49} x2={430} y2={49} />
    <Arrow x1={540} y1={49} x2={570} y2={49} />
    <Box x={290} y={130} label="Rejected" sub="any step before Received" tone="red" w={170} />
    <Arrow x1={205} y1={68} x2={290} y2={140} dashed />
    <Arrow x1={345} y1={68} x2={345} y2={130} dashed />
    <Arrow x1={485} y1={68} x2={420} y2={130} dashed />
    <text x={465} y={175} fontSize={9} fill="#475569" fontStyle="italic" textAnchor="middle">on Received → auto‑creates Consumables log entry</text>
  </svg>
);

const OfflineDiagram = () => (
  <svg viewBox="0 0 720 170" className="w-full mt-3" role="img" aria-label="Offline-first flow">
    <Box x={20} y={20} label="Edit form" sub="user types" tone="primary" />
    <Box x={170} y={20} label="Autosave draft" sub="localStorage" tone="emerald" />
    <Box x={330} y={20} label="Submit offline" sub="queued locally" tone="amber" />
    <Box x={490} y={20} label="Back online" sub="queue drains" tone="indigo" />
    <Box x={640} y={20} label="Server" sub="audit logged" tone="slate" w={70} />
    <Arrow x1={130} y1={39} x2={170} y2={39} />
    <Arrow x1={280} y1={39} x2={330} y2={39} />
    <Arrow x1={440} y1={39} x2={490} y2={39} />
    <Arrow x1={600} y1={39} x2={640} y2={39} />
  </svg>
);

const LocationPhotosDiagram = () => (
  <svg viewBox="0 0 720 190" className="w-full mt-3" role="img" aria-label="Equipment location and photos workflow">
    <Box x={20} y={20} label="Equipment Row" sub="MapPin / Camera" tone="primary" w={130} />
    <Box x={180} y={20} label="Location Tab" sub="lat / lng / GPS" tone="indigo" w={120} />
    <Box x={180} y={120} label="Photos Tab" sub="≤200 KB JPEG" tone="indigo" w={120} />
    <Arrow x1={150} y1={39} x2={180} y2={39} />
    <Arrow x1={150} y1={39} x2={180} y2={139} dashed />
    <Box x={330} y={20} label="Submit" sub="non-approver → pending" tone="amber" w={140} />
    <Box x={330} y={120} label="Submit photo" sub="pending / approved" tone="amber" w={140} />
    <Arrow x1={300} y1={39} x2={330} y2={39} />
    <Arrow x1={300} y1={139} x2={330} y2={139} />
    <Box x={500} y={20} label="Approve / Reject" sub="approver/admin" tone="emerald" w={130} />
    <Box x={500} y={120} label="Approve / Reject" sub="approver/admin" tone="emerald" w={130} />
    <Arrow x1={470} y1={39} x2={500} y2={39} />
    <Arrow x1={470} y1={139} x2={500} y2={139} />
    <Box x={650} y={20} label="Map link" sub="public/private" tone="slate" w={60} />
    <Box x={650} y={120} label="Lightbox" sub="grid icon" tone="slate" w={60} />
    <Arrow x1={630} y1={39} x2={650} y2={39} />
    <Arrow x1={630} y1={139} x2={650} y2={139} />
    <text x={360} y={180} textAnchor="middle" fontSize={9} fill="#475569" fontStyle="italic">Approver/Admin submissions auto-approve · others wait for review</text>
  </svg>
);

const ROLE_ROWS = [
  { role: 'Super Admin', who: 'IT / system owner', power: 'Unrestricted. Manages roles, approvals, branding, backups, purge.' },
  { role: 'Admin', who: 'Fleet manager', power: 'Day‑to‑day operations: equipment, operators, requests, purchases, mobilizations, billing.' },
  { role: 'Supervisor', who: 'Site supervisor', power: 'Manage equipment, operators, inspections, mobilizations, maintenance and consumables.' },
  { role: 'Approver', who: 'Operations head / accountant', power: 'Approve or reject equipment requests, inspections and purchase requests.' },
  { role: 'Requestor', who: 'Project site lead', power: 'Submit equipment requests and purchase requests; track their own.' },
  { role: 'Operator', who: 'Driver / heavy equipment op.', power: 'View assigned equipment & mobilizations; log consumables; submit purchase requests.' },
];

export default SystemGuide;
